import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormInputText, Skeleton } from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import type { SupportSettingCategory } from '../../../../../api/services/RiderSupportSettings';
import { useSupportSettingsCatalog } from '../../../hooks/useSupportSettingsCatalog';
import type { RiderFormValues, SupportSetting } from '../../../types';
import { getCategoryLabel, orderCategories } from '../helpers/orderCategories';
import { Pill, SkeletonPill } from '../primitives/Pill';

import { SubsectionLabel } from './styles';

const toRequiredMessage = (placeholder?: string): string => {
  if (!placeholder) {
    return 'Required field';
  }
  const stripped = placeholder.replace(/\.$/, '');

  return `Please ${stripped.charAt(0).toLowerCase()}${stripped.slice(1)}`;
};

type ReadyCatalogProps = {
  categories: SupportSettingCategory[];
  supportSettings: SupportSetting[];
  detailsMaxLength: number;
};

const ReadyCatalog = ({ categories, supportSettings, detailsMaxLength }: ReadyCatalogProps) => {
  const { control } = useFormContext<RiderFormValues>();
  const ordered = orderCategories(categories);

  return (
    <>
      {ordered.map((category) => {
        const selectedDetailSettings = category.supportSettings.filter((setting) => {
          if (!setting.hasDetails) {
            return false;
          }
          const entry = supportSettings.find((s) => s.supportType === setting.supportType);

          return Boolean(entry?.selected);
        });

        return (
          <CategoryBlock key={category.name}>
            <CategoryHeading>{getCategoryLabel(category.name)}</CategoryHeading>
            <PillGroup>
              {category.supportSettings.map((setting) => (
                <Pill key={setting.supportType} setting={setting} categoryName={category.name} />
              ))}
            </PillGroup>
            {selectedDetailSettings.length > 0 && (
              <DetailsBlock>
                {selectedDetailSettings.map((setting) => {
                  const index = supportSettings.findIndex((s) => s.supportType === setting.supportType);

                  if (index < 0) {
                    return null;
                  }

                  return (
                    <DetailField key={setting.supportType}>
                      <SubsectionLabel as="label" htmlFor={`setting-details-${setting.supportType}`}>
                        {setting.label} <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
                      </SubsectionLabel>
                      <Controller
                        control={control}
                        name={`supportSettings.${index}.details` as const}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <FormInputText
                            id={`setting-details-${setting.supportType}`}
                            name={`supportSettings.${index}.details`}
                            value={value ?? ''}
                            onChange={onChange}
                            onBlur={onBlur}
                            placeholder={setting.detailsPlaceholder}
                            variant="outlined"
                            multiline
                            rows={2}
                            inputProps={{ maxLength: detailsMaxLength, 'aria-required': true }}
                            error={!!error}
                            helperText={
                              error
                                ? toRequiredMessage(setting.detailsPlaceholder)
                                : `Max. ${detailsMaxLength} characters`
                            }
                            sx={{ width: '100%' }}
                          />
                        )}
                      />
                    </DetailField>
                  );
                })}
              </DetailsBlock>
            )}
          </CategoryBlock>
        );
      })}
    </>
  );
};

export const AdvancedRiderNotesSubsection = () => {
  const catalog = useSupportSettingsCatalog();
  const supportSettings = (useWatch<RiderFormValues, 'supportSettings'>({ name: 'supportSettings' }) ??
    []) as SupportSetting[];

  return (
    <Section>
      <SubsectionLabel>Rider requirements</SubsectionLabel>
      {(catalog.status === 'loading' || catalog.status === 'idle') && <CatalogSkeleton />}
      {catalog.status === 'error' && (
        <ErrorBanner role="alert">Rider requirements are temporarily unavailable.</ErrorBanner>
      )}
      {catalog.status === 'ready' && (
        <ReadyCatalog
          categories={catalog.categories}
          supportSettings={supportSettings}
          detailsMaxLength={catalog.detailsMaxLength}
        />
      )}
    </Section>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const CatalogSkeleton = () => (
  <CategoryBlock data-testid="catalog-skeleton">
    <CategoryHeading>
      <Skeleton variant="text" width={150} height={20} />
    </CategoryHeading>
    <PillGroup>
      <SkeletonPill variant="rounded" width={93} height={24} />
      <SkeletonPill variant="rounded" width={73} height={24} />
      <SkeletonPill variant="rounded" width={131} height={24} />
    </PillGroup>
    <CategoryHeading>
      <Skeleton variant="text" width={150} height={20} />
    </CategoryHeading>
    <PillGroup>
      <SkeletonPill variant="rounded" width={147} height={24} />
      <SkeletonPill variant="rounded" width={95} height={24} />
      <SkeletonPill variant="rounded" width={80} height={24} />
      <SkeletonPill variant="rounded" width={73} height={24} />
    </PillGroup>
    <CategoryHeading>
      <Skeleton variant="text" width={150} height={20} />
    </CategoryHeading>
    <PillGroup>
      <SkeletonPill variant="rounded" width={86} height={24} />
      <SkeletonPill variant="rounded" width={73} height={24} />
    </PillGroup>
  </CategoryBlock>
);

const ErrorBanner = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${colorTokens.warning.background};
  color: ${colorTokens.text.dark};
  font-size: 13px;
`;

const CategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryHeading = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: ${colorTokens.text.medium};
`;

const PillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const DetailsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 20px;
`;

const DetailField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RequiredAsterisk = styled.span`
  color: ${colorTokens.error.main};
`;
